import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { Rilievo, CreateRilievoInput, UpdateRilievoInput, Documento } from '../types';
import type { FilePronto } from '../utils/immagini';

export const rilieviKeys = {
  all: ['rilievi'] as const,
  list: (projectId: string) => [...rilieviKeys.all, projectId] as const,
};

export function useRilievi(projectId: string | undefined) {
  return useQuery({
    queryKey: rilieviKeys.list(projectId ?? ''),
    queryFn: () => api.get<Rilievo[]>(`/rilievi?projectId=${projectId}`),
    enabled: !!projectId,
  });
}

export function useCreaRilievo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRilievoInput) => api.post<Rilievo>('/rilievi', data),
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: rilieviKeys.list(v.projectId) });
    },
  });
}

export function useAggiornaRilievo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; projectId: string; data: UpdateRilievoInput }) =>
      api.patch<Rilievo>(`/rilievi/${v.id}`, v.data),
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: rilieviKeys.list(v.projectId) });
    },
  });
}

export function useEliminaRilievo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; projectId: string }) => api.delete(`/rilievi/${v.id}`),
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: rilieviKeys.list(v.projectId) });
    },
  });
}

/** Carica una foto già preparata (ridimensionata) e la lega al rilievo. */
export function useCaricaFotoRilievo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: { projectId: string; rilievoId: string; nome: string; file: FilePronto }) =>
      api.post<Documento>('/documenti', {
        projectId: v.projectId,
        rilievoId: v.rilievoId,
        nome: v.nome,
        categoria: 'rilievo',
        file: { data: v.file.data, mediaType: v.file.mediaType, filename: v.file.filename },
      }),
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: rilieviKeys.list(v.projectId) });
    },
  });
}
